/* ── Global Search Bar (MainLayout header) ────────────────────────────────
   Categorized, debounced, keyboard-navigable search over Teams, Players,
   and Games (including "Team A vs Team B" matchup queries).

   Behavior summary:
   - Query is debounced 300ms before hitting Supabase.
   - Results are flattened into one ordered list for Arrow/Enter navigation,
     while still rendering as visually distinct Teams/Players/Games panels.
   - Escape closes the dropdown; click-outside closes it too.
   - A request counter guards against out-of-order responses (a slow early
     keystroke resolving after a faster later one).
──────────────────────────────────────────────────────────────────────── */
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { Search, X, Loader2, User, Shield, Calendar } from "lucide-react";
import { runGlobalSearch } from "../utils/globalSearch";

const EMPTY_RESULTS = { teams: [], players: [], games: [] };

/** Formats a game row into a short, readable secondary line for the dropdown. */
function formatGameSecondary(game) {
  const dateLabel = game.game_date
    ? new Date(game.game_date).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "Date TBD";

  if (game.status === "final") {
    return `${dateLabel} · Final ${game.team1_score}-${game.team2_score}`;
  }
  return `${dateLabel} · ${game.seasons?.name ?? ""}`;
}

/**
 * Flattens categorized results into a single ordered array so ArrowUp/Down
 * and Enter can operate on one index, regardless of which panel it's in.
 */
function flattenResults(results) {
  const flat = [];

  results.teams.forEach((group) => {
    group.entries.forEach((entry) => {
      flat.push({
        type: "team",
        key: `team-${entry.id}`,
        route: `/teams/${entry.id}`,
        title: group.name,
        subtitle: `${entry.seasonName} · ${entry.wins}-${entry.losses}`,
        logoUrl: group.logoUrl,
      });
    });
  });

  results.players.forEach((p) => {
    flat.push({
      type: "player",
      key: `player-${p.id}`,
      route: `/players/${p.profile_id}`,
      title: p.name,
      subtitle: `#${p.jersey_number ?? "--"} · ${p.teams?.name ?? "Free agent"} · ${
        p.seasons?.name ?? ""
      }`,
    });
  });

  results.games.forEach((g) => {
    flat.push({
      type: "game",
      key: `game-${g.id}`,
      route: `/game/${g.id}`,
      title: `${g.team1?.name ?? "TBD"} vs ${g.team2?.name ?? "TBD"}`,
      subtitle: formatGameSecondary(g),
      team1LogoUrl: g.team1?.logo_url,
      team2LogoUrl: g.team2?.logo_url,
    });
  });

  return flat;
}

/** One selectable row, shared styling across all three categories. */
const ResultRow = ({ item, isActive, onSelect, onHover }) => (
  <button
    id={`search-option-${item.key}`}
    role="option"
    aria-selected={isActive}
    onMouseEnter={onHover}
    onClick={() => onSelect(item)}
    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-left transition-colors ${
      isActive
        ? "bg-orange-50 dark:bg-orange-950/40"
        : "hover:bg-gray-50 dark:hover:bg-gray-800/60"
    }`}
  >
    {item.type === "game" ? (
      <div className="flex items-center -space-x-2 shrink-0">
        {item.team1LogoUrl ? (
          <img
            src={item.team1LogoUrl}
            alt=""
            className="w-7 h-7 rounded-full border-2 border-white dark:border-gray-900 object-cover"
          />
        ) : (
          <div className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-800 border-2 border-white dark:border-gray-900" />
        )}
        {item.team2LogoUrl ? (
          <img
            src={item.team2LogoUrl}
            alt=""
            className="w-7 h-7 rounded-full border-2 border-white dark:border-gray-900 object-cover"
          />
        ) : (
          <div className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-800 border-2 border-white dark:border-gray-900" />
        )}
      </div>
    ) : item.logoUrl ? (
      <img
        src={item.logoUrl}
        alt=""
        className="w-8 h-8 rounded-full object-cover shrink-0"
      />
    ) : (
      <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0">
        {item.type === "player" ? (
          <User size={16} className="text-gray-400" />
        ) : (
          <Shield size={16} className="text-gray-400" />
        )}
      </div>
    )}
    <div className="min-w-0">
      <p className="text-sm font-semibold truncate text-gray-500 dark:text-white">
        {item.title}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-500 truncate">
        {item.subtitle}
      </p>
    </div>
  </button>
);

const CategoryPanel = ({ icon: Icon, label, children }) => (
  <div className="mb-1 last:mb-0">
    <div className="flex items-center gap-2 px-4 pt-3 pb-1.5">
      <Icon size={13} className="text-gray-400" />
      <p className="text-[11px] font-bold tracking-wider text-gray-400 uppercase">
        {label}
      </p>
    </div>
    <div className="space-y-0.5 px-2 pb-2">{children}</div>
  </div>
);

const GlobalSearchBar = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const requestIdRef = useRef(0);
  const debounceRef = useRef(null);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const flatResults = useMemo(() => flattenResults(results), [results]);
  const hasResults =
    results.teams.length > 0 ||
    results.players.length > 0 ||
    results.games.length > 0;

  // ── 300ms debounce: wait for typing to pause before hitting Supabase ──
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults(EMPTY_RESULTS);
      setLoading(false);
      setActiveIndex(-1);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      const thisRequestId = ++requestIdRef.current;
      setLoading(true);
      const data = await runGlobalSearch(trimmed);
      // Ignore stale responses if a newer keystroke has already fired.
      if (thisRequestId !== requestIdRef.current) return;
      setResults(data);
      setLoading(false);
      setActiveIndex(-1);
      setOpen(true);
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // ── Click-outside closes the dropdown ──
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const goToResult = useCallback(
    (item) => {
      if (!item) return;
      navigate(item.route);
      setOpen(false);
      setQuery("");
      setResults(EMPTY_RESULTS);
      setActiveIndex(-1);
      inputRef.current?.blur();
    },
    [navigate],
  );

  const handleKeyDown = (e) => {
    if (!open || flatResults.length === 0) {
      if (e.key === "Escape") {
        setOpen(false);
        inputRef.current?.blur();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault(); // stop the caret jumping to end of input
        setActiveIndex((i) => (i + 1) % flatResults.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex(
          (i) => (i - 1 + flatResults.length) % flatResults.length,
        );
        break;
      case "Enter": {
        e.preventDefault();
        // Enter with nothing highlighted yet selects the top result.
        const target = flatResults[activeIndex >= 0 ? activeIndex : 0];
        goToResult(target);
        break;
      }
      case "Escape":
        setOpen(false);
        setActiveIndex(-1);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  const clearQuery = () => {
    setQuery("");
    setResults(EMPTY_RESULTS);
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className="relative w-full">
      {/* ── Input ── */}
      <div className="relative">
        <Search
          size={16}
          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
        />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls="global-search-listbox"
          aria-activedescendant={
            activeIndex >= 0
              ? `search-option-${flatResults[activeIndex]?.key}`
              : undefined
          }
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => hasResults && setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search teams, players, or “Team A vs Team B”"
          className="w-full pl-10 pr-9 py-2.5 text-sm rounded-2xl border-2 border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
        />
        {loading ? (
          <Loader2
            size={15}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 animate-spin"
          />
        ) : query ? (
          <button
            onClick={clearQuery}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800"
            aria-label="Clear search"
          >
            <X size={13} />
          </button>
        ) : null}
      </div>

      {/* ── Dropdown overlay ── */}
      <AnimatePresence>
        {open && (
          <motion.div
            id="global-search-listbox"
            role="listbox"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15, ease: [0.23, 1, 0.32, 1] }}
            className="absolute left-0 right-0 mt-2 max-h-[70vh] overflow-y-auto bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-3xl shadow-2xl z-50 py-2"
          >
            {!hasResults && !loading ? (
              <p className="px-4 py-6 text-center text-sm text-red-500 dark:text-gray-400">
                No matches for "{query.trim()}"
              </p>
            ) : (
              <>
                {results.teams.length > 0 && (
                  <CategoryPanel icon={Shield} label="Teams">
                    {results.teams.map((group) =>
                      group.entries.map((entry) => {
                        const item = flatResults.find(
                          (f) => f.key === `team-${entry.id}`,
                        );
                        return (
                          <ResultRow
                            key={item.key}
                            item={item}
                            isActive={
                              flatResults[activeIndex]?.key === item.key
                            }
                            onSelect={goToResult}
                            onHover={() =>
                              setActiveIndex(flatResults.indexOf(item))
                            }
                          />
                        );
                      }),
                    )}
                  </CategoryPanel>
                )}

                {results.players.length > 0 && (
                  <CategoryPanel icon={User} label="Players">
                    {results.players.map((p) => {
                      const item = flatResults.find(
                        (f) => f.key === `player-${p.id}`,
                      );
                      return (
                        <ResultRow
                          key={item.key}
                          item={item}
                          isActive={flatResults[activeIndex]?.key === item.key}
                          onSelect={goToResult}
                          onHover={() =>
                            setActiveIndex(flatResults.indexOf(item))
                          }
                        />
                      );
                    })}
                  </CategoryPanel>
                )}

                {results.games.length > 0 && (
                  <CategoryPanel icon={Calendar} label="Games">
                    {results.games.map((g) => {
                      const item = flatResults.find(
                        (f) => f.key === `game-${g.id}`,
                      );
                      return (
                        <ResultRow
                          key={item.key}
                          item={item}
                          isActive={flatResults[activeIndex]?.key === item.key}
                          onSelect={goToResult}
                          onHover={() =>
                            setActiveIndex(flatResults.indexOf(item))
                          }
                        />
                      );
                    })}
                  </CategoryPanel>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GlobalSearchBar;
